import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { HttpCancelableRequest } from '../../Common/Data/base.repository';
import { AttachmentBundlerRepository } from './attachmentBundler.repository';
import { GetEntitiesRequest, GetFurtherLimitRequest, ReportDetail } from '../../Reporting/Manager/report.manager.model';
import { AttachmentBundlerContext } from '../attachment.modal.model';

const ALL_ATTACHMENT_LOCATIONS: Core.AttachmentBundlerLocationEnum[] = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];
export const ALL_PROPERTY_TYPES: Core.PropertyTypes[] = [1, 2, 3, 4];

export const ATTACHMENT_LOCATION_VISIBLE_COLUMNS: Record<string, string[]> = {
    Company: ['attachmentType', 'image', 'companyName'],
    Site: ['attachmentType', 'image', 'siteName', 'state'],
    Parcel: ['attachmentType', 'image', 'parcelAccountNumber', 'propertyType', 'state'],
    'PP Return': [
        'attachmentType',
        'image',
        'parcelAccountNumber',
        'propertyType',
        'state',
        'year',
        'additionalDetails',
    ],
    'Asmt Rev': [
        'attachmentType',
        'image',
        'parcelAccountNumber',
        'propertyType',
        'state',
        'year',
        'additionalDetails',
    ],
    Appeal: ['attachmentType', 'image', 'parcelAccountNumber', 'propertyType', 'state', 'year', 'additionalDetails'],
    'Tax Bill': [
        'attachmentType',
        'image',
        'parcelAccountNumber',
        'propertyType',
        'state',
        'collectorName',
        'year',
        'additionalDetails',
    ],
    'Tax Payment': [
        'attachmentType',
        'image',
        'parcelAccountNumber',
        'propertyType',
        'state',
        'collectorName',
        'year',
        'additionalDetails',
    ],
    Refund: ['attachmentType', 'image', 'parcelAccountNumber', 'propertyType', 'state', 'collectorName', 'year'],
    Budget: ['attachmentType', 'image', 'parcelAccountNumber', 'propertyType', 'state', 'collectorName', 'year'],
    'Filing Batch': ['attachmentType', 'image', 'topLevelCompanyName', 'state', 'year', 'additionalDetails'],
    Invoice: ['attachmentType', 'image', 'companyName', 'state', 'year', 'additionalDetails'],
    'Payment Batch': ['attachmentType', 'image', 'companyName', 'additionalDetails'],
    'Document Intake': ['attachmentType', 'image', 'additionalDetails'],
    State: ['attachmentType', 'image', 'state'],
    Assessor: ['attachmentType', 'image', 'state', 'assessorName'],
    'Form Revision': ['attachmentType', 'image', 'state', 'additionalDetails'],
    Collector: ['attachmentType', 'image', 'state', 'collectorName'],
    'Tax Rate Area': ['attachmentType', 'image', 'state', 'additionalDetails'],
    'Tax Authority': ['attachmentType', 'image', 'state', 'assessorName', 'collectorName', 'additionalDetails'],
};

/**
 * Columns that are relevant to a given attachment location but should NOT be shown by default.
 * These are made available to the user (hidden by default) so they can be enabled via the
 * grid's column tool panel. This corresponds to the "orange" cells in the bundler column spec.
 */
export const ATTACHMENT_LOCATION_AVAILABLE_COLUMNS: Record<string, string[]> = {
    Company: ['attachmentDescription', 'topLevelCompanyName', 'year'],
    Site: ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'year'],
    Parcel: [
        'attachmentDescription',
        'topLevelCompanyName',
        'companyName',
        'siteName',
        'assessorName',
        'additionalDetails',
        'year',
    ],
    'PP Return': ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'siteName', 'assessorName'],
    'Asmt Rev': ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'siteName', 'assessorName'],
    Appeal: ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'siteName', 'assessorName'],
    'Tax Bill': ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'siteName', 'assessorName'],
    'Tax Payment': ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'siteName', 'assessorName'],
    Refund: ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'siteName', 'assessorName'],
    Budget: ['attachmentDescription', 'topLevelCompanyName', 'companyName', 'siteName', 'assessorName'],
    'Filing Batch': ['attachmentDescription'],
    Invoice: ['attachmentDescription', 'topLevelCompanyName', 'siteName'],
    'Payment Batch': ['attachmentDescription', 'topLevelCompanyName', 'year'],
    'Document Intake': [
        'attachmentDescription',
        'topLevelCompanyName',
        'companyName',
        'siteName',
        'parcelAccountNumber',
        'propertyType',
        'state',
        'assessorName',
        'year',
    ],
    State: ['attachmentDescription', 'year'],
    Assessor: ['attachmentDescription', 'year'],
    'Form Revision': ['attachmentDescription', 'year'],
    Collector: ['attachmentDescription', 'year'],
    'Tax Rate Area': ['attachmentDescription', 'assessorName', 'collectorName', 'year'],
    'Tax Authority': ['attachmentDescription', 'year'],
};

interface OutputParams {
    outputType: Core.AttachmentBundlerGenerateFileOutputTypeEnum;
    includeManifest?: boolean;
    manifestFields?: Core.AttachmentBundlerItemPropertyEnum[];
}

@Injectable()
export class AttachmentBundlerService {
    constructor(private readonly _attachmentBundlerRepository: AttachmentBundlerRepository) {}

    private _masterAttachmentList: Core.AttachmentBundlerItemModel[] = [];
    private _attachmentListDataSubject: BehaviorSubject<Core.AttachmentBundlerItemModel[]> = new BehaviorSubject([]);
    private _attachmentListData$: Observable<Core.AttachmentBundlerItemModel[]> =
        this._attachmentListDataSubject.asObservable();
    private _selectionListDataSubject: BehaviorSubject<Core.AttachmentBundlerGenerateFileItemModel[]> =
        new BehaviorSubject([]);
    private _selectionListData$: Observable<Core.AttachmentBundlerGenerateFileItemModel[]> =
        this._selectionListDataSubject.asObservable();
    private _loadingSubject: Subject<boolean> = new Subject();
    private _loading$: Observable<boolean> = this._loadingSubject.asObservable();

    private _selectedAttachmentData: Core.AttachmentBundlerGenerateFileItemModel[] = [];
    private _activeLoadRequest: HttpCancelableRequest<Core.AttachmentBundlerItemModel[]> | null = null;

    private _hasActiveFilter: boolean;
    private _report: ReportDetail;
    private _alreadyAttachedIds: Set<string> = new Set();
    private _dialogContext: AttachmentBundlerContext | null = null;
    private _currentFilter: Core.AttachmentBundlerSearchModel = {
        attachmentTypes: [],
        endTaxYear: null,
        entityFilters: [],
        excludeInactive: true,
        includeBlankTaxYear: false,
        locations: ALL_ATTACHMENT_LOCATIONS,
        propertyTypes: ALL_PROPERTY_TYPES,
        scope: Core.AttachmentBundlerScopeEnum.Company,
        sitePropCharFilters: [],
        sourceEntityIds: [],
        sourceEntityIsTopLevelCompany: false,
        sourceEntityTypeID: null,
        startTaxYear: null,
    };

    private _outputParams: OutputParams = {
        outputType: null,
        includeManifest: false,
        manifestFields: [],
    };

    setSearchScope(scope: Core.AttachmentBundlerScopeEnum) {
        this._currentFilter.scope = this._normalizeScope(scope);
    }

    setAlreadyAttachedIds(ids: string[]): void {
        this._alreadyAttachedIds = new Set(ids);
    }

    isAlreadyAttached(attachmentId: string): boolean {
        return this._alreadyAttachedIds.has(attachmentId);
    }

    setDialogContext(context: AttachmentBundlerContext): void {
        this._dialogContext = context;
    }

    get dialogContext(): AttachmentBundlerContext | null {
        return this._dialogContext;
    }

    get loading$(): Observable<boolean> {
        return this._loading$;
    }

    get selectedAttachments$(): Observable<Core.AttachmentBundlerGenerateFileItemModel[]> {
        return this._selectionListData$;
    }

    get attachmentList$(): Observable<Core.AttachmentBundlerItemModel[]> {
        return this._attachmentListData$;
    }

    get outputFormValid(): boolean {
        return this._outputParams.outputType !== null;
    }

    get hasActiveFilter(): boolean {
        return this._hasActiveFilter;
    }

    get filterReport(): ReportDetail {
        return this._report;
    }

    getAttachmentsFromEntityFilter(entityRequest: GetEntitiesRequest, report: ReportDetail): void {
        this._report = report;
        this._hasActiveFilter = true;

        this._currentFilter.sourceEntityIds = entityRequest.sourceEntityIDs;
        this._currentFilter.sourceEntityTypeID = entityRequest.sourceEntityTypeID;
        this._currentFilter.sourceEntityIsTopLevelCompany = entityRequest.sourceEntityIsTopLevelCompany;
        this._currentFilter.sitePropCharFilters = entityRequest.sitePropCharFilters as any;
        this._currentFilter.entityFilters = entityRequest.entityFilters;
        this._currentFilter.excludeInactive = entityRequest.excludeInactive;
    }

    async getAttachmentsFromFurtherLimitFilter(
        furtherLimitRequest: GetFurtherLimitRequest,
        report: ReportDetail,
    ): Promise<boolean> {
        this._report = report;
        this._hasActiveFilter = true;

        this._currentFilter.includeBlankTaxYear = furtherLimitRequest.includeBlankTaxYear;
        this._currentFilter.startTaxYear = furtherLimitRequest.startTaxYear;
        this._currentFilter.endTaxYear = furtherLimitRequest.endTaxYear;
        this._currentFilter.locations =
            furtherLimitRequest.attachmentLocations?.length > 0
                ? furtherLimitRequest.attachmentLocations
                : ALL_ATTACHMENT_LOCATIONS;
        this._currentFilter.attachmentTypes = furtherLimitRequest.attachmentTypes;
        this._currentFilter.propertyTypes =
            this._currentFilter.scope === Core.AttachmentBundlerScopeEnum.Jurisdiction
                ? []
                : furtherLimitRequest.propertyTypes?.length > 0
                    ? furtherLimitRequest.propertyTypes
                    : ALL_PROPERTY_TYPES;

        return await this.refreshAttachments();
    }

    async refreshAttachments(): Promise<boolean> {
        this.cancelActiveLoad();
        this._loadingSubject.next(true);

        const request = this._attachmentBundlerRepository.getList(this._currentFilter);
        this._activeLoadRequest = request;

        try {
            const result = await request.execute();
            if (!result.completed || request.isCanceled) {
                return false;
            }

            this._masterAttachmentList = result.value || [];
            this._emitFilteredAttachmentList();
            return true;
        } finally {
            if (this._activeLoadRequest === request) {
                this._activeLoadRequest = null;
                this._loadingSubject.next(false);
            }
        }
    }

    setSelectedAttachments(selected: Core.AttachmentBundlerGenerateFileItemModel[]): void {
        this._selectedAttachmentData = selected;
        this._selectionListDataSubject.next(this._selectedAttachmentData);
        this._emitFilteredAttachmentList();
    }

    updateOutputType(outputType: Core.AttachmentBundlerGenerateFileOutputTypeEnum) {
        this._outputParams.outputType = outputType;
    }

    updateIncludeManifest(includeManifest: boolean, manifestFields: Core.AttachmentBundlerItemPropertyEnum[]): void {
        this._outputParams.includeManifest = includeManifest;
        this._outputParams.manifestFields = manifestFields;
    }

    async bundle(): Promise<void> {
        this._loadingSubject.next(true);

        const searchValue: Core.AttachmentBundlerGenerateFile = {
            outputType: this._outputParams.outputType,
            includeManifest: this._outputParams.includeManifest,
            manifestFields: this._outputParams.manifestFields,
            attachmentInfo: this._selectedAttachmentData,
        };

        try {
            await lastValueFrom(this._attachmentBundlerRepository.bundle(searchValue));
        } finally {
            this._loadingSubject.next(false);
        }
    }

    cancelActiveLoad(): boolean {
        const activeRequest = this._activeLoadRequest;
        if (!activeRequest || activeRequest.isCanceled) {
            return false;
        }

        this._activeLoadRequest = null;
        activeRequest.cancel();
        this._loadingSubject.next(false);
        return true;
    }

    private _emitFilteredAttachmentList(): void {
        const selectedIds = new Set(this._selectedAttachmentData.map((y) => y.attachmentId));
        const filteredList = this._masterAttachmentList.filter((x) => !selectedIds.has(x.attachmentId));
        this._attachmentListDataSubject.next(filteredList);
    }

    private _normalizeScope(scope: Core.AttachmentBundlerScopeEnum): Core.AttachmentBundlerScopeEnum {
        if (
            scope === Core.AttachmentBundlerScopeEnum.Invoice ||
            scope === Core.AttachmentBundlerScopeEnum.LitigationReferral
        ) {
            return Core.AttachmentBundlerScopeEnum.Company;
        }

        return scope;
    }
}
